import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import LoginForm from '@/components/auth/LoginForm';
import SignUpForm from '@/components/auth/SignUpForm';
import GoogleAuthButton from '@/components/auth/GoogleAuthButton';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Code, Cpu, Server, Laptop, Database, Globe, Hash, Share2, Users, MessageSquare, Zap, BarChart } from 'lucide-react';
import { testConnection, supabase, ensureConnection } from "@/integrations/supabase/client";
import Logo from '@/components/Logo';
import logger from '@/lib/logger';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import useErrorHandler from '@/hooks/use-error-handler';

export default function Login() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'signup' ? 'signup' : 'login';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const [connectionStatus, setConnectionStatus] = useState<{
    connected: boolean;
    error?: string;
  }>({ connected: true });
  const [processingOAuth, setProcessingOAuth] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [connectionError, setConnectionError] = useState(false);

  logger.debug('Rendering Login component', { 
    module: 'Login',
    data: { defaultTab, activeTab }
  });

  // Check for hash in URL which indicates an OAuth redirect
  useEffect(() => {
    const handleOAuthFragment = async () => {
      // Check if we have a hash with auth data
      if (window.location.hash && window.location.hash.includes('access_token')) {
        try {
          logger.info('Found OAuth hash fragment on login page', { 
            module: 'Login',
            data: { 
              hash: window.location.hash.substring(0, 50) + '...' // Only log part of it for security
            }
          });
          
          // Wrap this in a try/catch to handle any potential errors
          try {
            // Process the hash fragment explicitly - most reliable method
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const accessToken = hashParams.get('access_token');
            const refreshToken = hashParams.get('refresh_token');
            
            if (accessToken) {
              logger.info('Found access token in hash, setting session manually', { 
                module: 'Login'
              });
              
              // Set the session directly with the extracted token
              await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken || '',
              });
            }
          } catch (e) {
            logger.error('Error processing hash params directly', {
              module: 'Login',
              data: e
            });
            
            // Fallback to the standard getSession method
            await supabase.auth.getSession();
          }
          
          // Check if we have a session now
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            logger.error('Error getting session from hash', {
              module: 'Login',
              data: error
            });
            return;
          }
          
          if (data.session) {
            logger.info('Successfully got session from hash', {
              module: 'Login',
              data: { 
                userId: data.session.user.id,
                provider: data.session.user.app_metadata.provider 
              }
            });
            
            // Clean URL and redirect to home
            window.history.replaceState({}, document.title, '/home');
            navigate('/home', { replace: true });
          } else {
            logger.warn('No session found after processing hash', {
              module: 'Login'
            });
          }
        } catch (err) {
          logger.error('Error processing OAuth hash', {
            module: 'Login',
            data: err
          });
        }
      }
    };
    
    handleOAuthFragment();
  }, [navigate]);

  // Handle OAuth callback if present in URL
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const isCallback = searchParams.get('callback') === 'google';
      const hasHash = window.location.hash && (
        window.location.hash.includes('access_token') || 
        window.location.hash.includes('error=') || 
        window.location.hash.includes('type=recovery')
      );
      
      // Check if we're in a local development environment
      const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      logger.debug('Checking for OAuth callback', { 
        module: 'Login', 
        data: { isCallback, hasHash, hash: window.location.hash, isLocalDevelopment } 
      });
      
      if ((isCallback || hasHash) && !user) {
        setProcessingOAuth(true);
        logger.info('Processing OAuth callback', { module: 'Login' });
        
        try {
          // Handle hash fragments by explicitly parsing them if needed
          if (hasHash && window.location.hash.includes('access_token')) {
            // For local development, try a more direct approach with the hash
            if (isLocalDevelopment) {
              try {
                // Extract tokens manually from hash
                const hashParams = new URLSearchParams(window.location.hash.substring(1));
                const accessToken = hashParams.get('access_token');
                const refreshToken = hashParams.get('refresh_token');
                
                if (accessToken) {
                  logger.info('Local development: setting session with extracted tokens', { module: 'Login' });
                  // Set the session directly with the extracted tokens
                  await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken || '',
                  });
                }
              } catch (e) {
                logger.error('Error extracting tokens from hash in local development', { 
                  module: 'Login',
                  data: e
                });
              }
            }
            
            // This ensures Supabase also sees the hash fragment
            await supabase.auth.getSession();
            
            // Give the auth system a moment to process the token
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
          // Get session from URL or session storage
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            throw error;
          }
          
          if (data.session) {
            logger.info('OAuth login successful, found session', { 
              module: 'Login',
              data: { userId: data.session.user.id }
            });
            
            // Clear the URL parameters/hash to prevent issues on refresh
            window.history.replaceState({}, document.title, '/home');
            
            // Navigate to home
            navigate('/home', { replace: true });
          } else {
            // Special handling for local development - one more attempt with a delay
            if (isLocalDevelopment && hasHash) {
              logger.info('Local development: retrying session fetch after delay', { module: 'Login' });
              
              // Wait a bit longer for the session to be available
              await new Promise(resolve => setTimeout(resolve, 2000));
              
              // Try once more
              const retryResult = await supabase.auth.getSession();
              
              if (retryResult.data?.session) {
                logger.info('Local development: session found on retry', { 
                  module: 'Login',
                  data: { userId: retryResult.data.session.user.id }
                });
                
                // Clean URL and navigate
                window.history.replaceState({}, document.title, '/home');
                navigate('/home', { replace: true });
                return;
              }
            }
            
            logger.error('No session found after OAuth callback', { module: 'Login' });
            // Clean the URL
            window.history.replaceState({}, document.title, '/login');
            setProcessingOAuth(false);
          }
        } catch (err) {
          handleError(err, 'OAuth login failed', { module: 'Login' });
          // Clean the URL
          window.history.replaceState({}, document.title, '/login');
          setProcessingOAuth(false);
        }
      }
    };
    
    handleOAuthCallback();
  }, [searchParams, navigate, handleError, user]);

  // Redirect to home if user is already logged in
  useEffect(() => {
    if (!loading && user) {
      logger.info('User is already authenticated, redirecting to home', {
        module: 'Login'
      });
      navigate('/home');
    }
  }, [user, loading, navigate]);

  // Test connection to Supabase
  useEffect(() => {
    const checkConnection = async () => {
      try {
        // Add a timeout to the connection test
        const timeoutPromise = new Promise<any>((_, reject) => 
          setTimeout(() => reject(new Error('Connection test timed out')), 3000)
        );
        
        const connectionTestPromise = testConnection();
        
        // Race between connection test and timeout
        const result = await Promise.race([
          connectionTestPromise,
          timeoutPromise
        ]);
        
        setConnectionStatus({
          connected: result.connected,
          error: !result.connected ? result.error : undefined
        });
      } catch (err) {
        handleError(err, 'Connection check failed', {
          module: 'Login'
        });
        setConnectionStatus({
          connected: false,
          error: err instanceof Error ? err.message : 'Unknown connection error'
        });
      }
    };
    
    checkConnection();
  }, [handleError]);

  // Add function to test and reconnect
  const handleReconnect = async () => {
    setIsCheckingConnection(true);
    setConnectionError(false);
    
    try {
      const result = await testConnection();
      
      if (result.connected) {
        // Ensure connection is fully established
        await ensureConnection();
        setConnectionError(false);
      } else {
        setConnectionError(true);
      }
    } catch (error) {
      console.error("Error reconnecting:", error);
      setConnectionError(true);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  // Add useEffect to check connection on load
  useEffect(() => {
    handleReconnect();
  }, []);

  // If processing OAuth or loading, show loading indicator
  if (loading || processingOAuth) {
    return <LoadingSpinner fullScreen text={processingOAuth ? "Completing login..." : "Loading..."} />;
  }

  return (
    <div className="relative flex flex-col min-h-screen bg-vibebops-background overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-vibebops-background via-black to-vibebops-background opacity-80 animate-[gradient_8s_ease_infinite]"></div>
        
        {/* Circuit board pattern */}
        <div className="absolute inset-0 opacity-15" 
          style={{ 
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none'/%3E%3Cpath d='M10 10h10v10H10zM30 10h10v10H30zM50 10h10v10H50zM70 10h10v10H70zM90 10h10v10H90zM10 30h10v10H10zM30 30h10v10H30zM50 30h10v10H50zM70 30h10v10H70zM90 30h10v10H90zM10 50h10v10H10zM30 50h10v10H30zM50 50h10v10H50zM70 50h10v10H70zM90 50h10v10H90zM10 70h10v10H10zM30 70h10v10H30zM50 70h10v10H50zM70 70h10v10H70zM90 70h10v10H90zM10 90h10v10H10zM30 90h10v10H30zM50 90h10v10H50zM70 90h10v10H70zM90 90h10v10H90z' stroke='%23FF6E50' stroke-opacity='0.2' stroke-width='1' fill='none'/%3E%3Cpath d='M0 0h10v10H0zM20 0h10v10H20zM40 0h10v10H40zM60 0h10v10H60zM80 0h10v10H80zM0 20h10v10H0zM20 20h10v10H20zM40 20h10v10H40zM60 20h10v10H60zM80 20h10v10H80zM0 40h10v10H0zM20 40h10v10H20zM40 40h10v10H40zM60 40h10v10H60zM80 40h10v10H80zM0 60h10v10H0zM20 60h10v10H20zM40 60h10v10H40zM60 60h10v10H60zM80 60h10v10H80zM0 80h10v10H0zM20 80h10v10H20zM40 80h10v10H40zM60 80h10v10H60zM80 80h10v10H80z' stroke='%235D00FF' stroke-opacity='0.15' stroke-width='1' fill='none'/%3E%3C/svg%3E")`,
            backgroundSize: '300px 300px'
          }}>
        </div>

        {/* Floating elements with parallax effect */}
        <div className="absolute inset-0">
          {/* Floating icons with animation */}
          <div className="absolute top-[15%] left-[10%] text-vibebops-accent/20 animate-float" style={{ '--float-offset': '15px', animationDelay: '0s' } as React.CSSProperties}>
            <Code size={48} />
          </div>
          <div className="absolute top-[35%] right-[15%] text-vibebops-accent/15 animate-float" style={{ '--float-offset': '20px', animationDelay: '1.5s' } as React.CSSProperties}>
            <Cpu size={64} />
          </div>
          <div className="absolute bottom-[20%] left-[20%] text-vibebops-accent2/20 animate-float" style={{ '--float-offset': '12px', animationDelay: '0.8s' } as React.CSSProperties}>
            <Server size={40} />
          </div>
          <div className="absolute top-[60%] right-[25%] text-vibebops-accent/10 animate-float" style={{ '--float-offset': '18px', animationDelay: '2.2s' } as React.CSSProperties}>
            <Globe size={56} />
          </div>
          <div className="absolute bottom-[30%] right-[10%] text-vibebops-accent2/15 animate-float" style={{ '--float-offset': '10px', animationDelay: '1.1s' } as React.CSSProperties}>
            <Hash size={48} />
          </div>
          <div className="absolute top-[75%] left-[5%] text-white/10 animate-float" style={{ '--float-offset': '25px', animationDelay: '0.3s' } as React.CSSProperties}>
            <Laptop size={72} />
          </div>
        </div>
        
        {/* Interactive network visualization */}
        <div className="absolute inset-0 opacity-15">
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Node points */}
            <circle cx="20" cy="20" r="1.5" fill="#FF6E50" className="animate-pulse-subtle" style={{ animationDelay: '0s' }}/>
            <circle cx="80" cy="15" r="1.5" fill="#FF6E50" className="animate-pulse-subtle" style={{ animationDelay: '0.7s' }}/>
            <circle cx="50" cy="50" r="2" fill="#FF6E50" className="animate-pulse-subtle" style={{ animationDelay: '1.4s' }}/>
            <circle cx="85" cy="75" r="1.5" fill="#FF6E50" className="animate-pulse-subtle" style={{ animationDelay: '2.1s' }}/>
            <circle cx="15" cy="85" r="1.5" fill="#FF6E50" className="animate-pulse-subtle" style={{ animationDelay: '2.8s' }}/>
            <circle cx="40" cy="30" r="1.2" fill="#9D4EDD" className="animate-pulse-subtle" style={{ animationDelay: '0.4s' }}/>
            <circle cx="60" cy="70" r="1.2" fill="#9D4EDD" className="animate-pulse-subtle" style={{ animationDelay: '1.8s' }}/>
            <circle cx="30" cy="65" r="1.2" fill="#9D4EDD" className="animate-pulse-subtle" style={{ animationDelay: '3.2s' }}/>
            
            {/* Connection lines with animation */}
            <line x1="20" y1="20" x2="50" y2="50" stroke="#FF6E50" strokeWidth="0.2" strokeDasharray="1,1" className="animate-line-draw" style={{ animationDelay: '0.2s' } as React.CSSProperties}/>
            <line x1="80" y1="15" x2="50" y2="50" stroke="#FF6E50" strokeWidth="0.2" strokeDasharray="1,1" className="animate-line-draw" style={{ animationDelay: '0.5s' } as React.CSSProperties}/>
            <line x1="85" y1="75" x2="50" y2="50" stroke="#FF6E50" strokeWidth="0.2" strokeDasharray="1,1" className="animate-line-draw" style={{ animationDelay: '0.8s' } as React.CSSProperties}/>
            <line x1="15" y1="85" x2="50" y2="50" stroke="#FF6E50" strokeWidth="0.2" strokeDasharray="1,1" className="animate-line-draw" style={{ animationDelay: '1.1s' } as React.CSSProperties}/>
            <line x1="40" y1="30" x2="20" y2="20" stroke="#9D4EDD" strokeWidth="0.2" strokeDasharray="1,1" className="animate-line-draw" style={{ animationDelay: '1.4s' } as React.CSSProperties}/>
            <line x1="60" y1="70" x2="85" y2="75" stroke="#9D4EDD" strokeWidth="0.2" strokeDasharray="1,1" className="animate-line-draw" style={{ animationDelay: '1.7s' } as React.CSSProperties}/>
            <line x1="30" y1="65" x2="15" y2="85" stroke="#9D4EDD" strokeWidth="0.2" strokeDasharray="1,1" className="animate-line-draw" style={{ animationDelay: '2.0s' } as React.CSSProperties}/>
            <line x1="40" y1="30" x2="80" y2="15" stroke="#9D4EDD" strokeWidth="0.2" strokeDasharray="1,1" className="animate-line-draw" style={{ animationDelay: '2.3s' } as React.CSSProperties}/>
            <line x1="60" y1="70" x2="30" y2="65" stroke="#9D4EDD" strokeWidth="0.2" strokeDasharray="1,1" className="animate-line-draw" style={{ animationDelay: '2.6s' } as React.CSSProperties}/>
          </svg>
        </div>
        
        {/* Data packets animated along the network */}
        <div className="absolute inset-0">
          <div className="absolute top-[35%] left-[35%] w-1 h-1 bg-vibebops-accent rounded-full animate-data-packet" style={{ '--path-x': '15%', '--path-y': '15%', animationDelay: '0s' } as React.CSSProperties}></div>
          <div className="absolute top-[65%] left-[65%] w-1 h-1 bg-vibebops-accent rounded-full animate-data-packet" style={{ '--path-x': '15%', '--path-y': '-15%', animationDelay: '0.5s' } as React.CSSProperties}></div>
          <div className="absolute top-[50%] left-[25%] w-1 h-1 bg-purple-400 rounded-full animate-data-packet" style={{ '--path-x': '25%', '--path-y': '0%', animationDelay: '1s' } as React.CSSProperties}></div>
          <div className="absolute top-[25%] left-[75%] w-1 h-1 bg-purple-400 rounded-full animate-data-packet" style={{ '--path-x': '-25%', '--path-y': '25%', animationDelay: '1.5s' } as React.CSSProperties}></div>
        </div>
        
        {/* Neubrutalist decorative elements with 3D effect */}
        <div className="absolute top-[5%] left-[30%] w-20 h-20 border-4 border-black bg-vibebops-accent/30 rounded-xl shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transform rotate-12 hover:rotate-0 transition-transform duration-700"></div>
        <div className="absolute bottom-[10%] right-[30%] w-16 h-16 border-4 border-black bg-vibebops-accent2/20 rounded-xl shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transform -rotate-6 hover:rotate-0 transition-transform duration-700"></div>
        <div className="absolute top-[50%] left-[85%] w-16 h-16 border-4 border-black bg-purple-500/20 rounded-full shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all duration-700"></div>
        
        {/* Futuristic UI elements */}
        <div className="absolute top-[10%] right-[10%] font-mono text-xs text-vibebops-accent/20 text-right">
          <div className="mb-1 flex items-center justify-end">
            <div className="w-20 h-1 bg-vibebops-accent/20 mr-1 rounded-full overflow-hidden">
              <div className="h-full w-3/4 bg-vibebops-accent animate-pulse-subtle"></div>
            </div>
            <span>75%</span>
          </div>
          <div className="mb-1 flex items-center justify-end">
            <div className="w-20 h-1 bg-vibebops-accent/20 mr-1 rounded-full overflow-hidden">
              <div className="h-full w-1/2 bg-vibebops-accent animate-pulse-subtle" style={{ animationDelay: '0.3s' }}></div>
            </div>
            <span>50%</span>
          </div>
          <div className="flex items-center justify-end">
            <div className="w-20 h-1 bg-vibebops-accent/20 mr-1 rounded-full overflow-hidden">
              <div className="h-full w-1/4 bg-vibebops-accent animate-pulse-subtle" style={{ animationDelay: '0.6s' }}></div>
            </div>
            <span>25%</span>
          </div>
        </div>
        
        {/* Code blocks */}
        <div className="absolute top-[25%] left-[65%] text-vibebops-accent/10 font-mono text-[8px] leading-tight p-2 border border-vibebops-accent/10 rounded">
          <div>{'function connect() {'}</div>
          <div>&nbsp;&nbsp;{'return new Promise((resolve) => {'}</div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;{'const network = initNetwork();'}</div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;{'network.onReady(() => {'}</div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{'resolve(network);'}</div>
          <div>&nbsp;&nbsp;&nbsp;&nbsp;{'});'}</div>
          <div>&nbsp;&nbsp;{'});'}</div>
          <div>{'}'}</div>
        </div>
        
        <div className="absolute bottom-[45%] left-[10%] text-vibebops-accent/10 font-mono text-[8px] leading-tight p-2 border border-vibebops-accent/10 rounded">
          <div>{'npm install @vibebops/core'}</div>
          <div>{'npm install @vibebops/ui'}</div>
          <div>{'npm install @vibebops/network'}</div>
          <div className="animate-typing overflow-hidden whitespace-nowrap w-[100px]" style={{ borderRight: '1px solid', animationIterationCount: 'infinite' } as React.CSSProperties}>
            {'> initializing...'}
          </div>
        </div>
      </div>
      
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-12">
        {/* Logo with animation */}
        <div className="mb-2 flex flex-col items-center">
          <div className="animate-pulse-glow relative">
            <div className="transform transition-transform hover:scale-110 duration-300">
              <Logo size="xxlarge" />
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-3 -right-3 w-2 h-2 bg-vibebops-accent rounded-full opacity-70 animate-ping"></div>
            <div className="absolute -bottom-2 -left-1 w-1.5 h-1.5 bg-blue-400 rounded-full opacity-60 animate-ping" style={{ animationDuration: '2.5s' }}></div>
          </div>
          <h1 className="text-center mt-0 text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-vibebops-accent to-purple-500">
            VibeBops
          </h1>
          <p className="text-vibebops-textMuted text-sm mt-2 animate-pulse-subtle">Share your no-code apps</p>
        </div>
        
        <div className="w-full max-w-md relative">
          {connectionError && (
            <div className="mb-6 p-4 border border-red-500 rounded-md bg-red-50 text-red-700">
              <h3 className="text-lg font-semibold mb-2">Connection Error</h3>
              <p className="mb-3">Unable to connect to the server. This may affect login functionality.</p>
              <button
                onClick={handleReconnect}
                disabled={isCheckingConnection}
                className="px-4 py-2 bg-vibebops-accent text-white rounded-md hover:bg-vibebops-accent/90 disabled:opacity-50"
              >
                {isCheckingConnection ? "Checking Connection..." : "Reconnect"}
              </button>
            </div>
          )}

          {!connectionStatus.connected && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Connection Error</AlertTitle>
              <AlertDescription>
                {connectionStatus.error || "Cannot connect to the server. Please try again later."}
              </AlertDescription>
            </Alert>
          )}

          <Card className="border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,0.8),0_0_15px_rgba(255,110,80,0.7)]">
            <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
              <CardHeader>
                <TabsList className="grid w-full grid-cols-2 p-1.5 bg-vibebops-highlight border-2 border-black rounded-md shadow-inner overflow-hidden relative gap-2">
                  {/* Subtle grid background */}
                  <div className="absolute inset-0 opacity-5" 
                    style={{ 
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M0 0h20v20H0V0zm10 10h10v10H10V10zM0 10h10v10H0V10z'/%3E%3C/g%3E%3C/svg%3E")`,
                      backgroundSize: '8px 8px'
                    }}>
                  </div>
                  <TabsTrigger value="login">Login</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>
              </CardHeader>
              
              <CardContent>
                <TabsContent value="login">
                  <LoginForm showPassword={showPassword} setShowPassword={setShowPassword} />
                </TabsContent>
                
                <TabsContent value="signup">
                  <SignUpForm showPassword={showPassword} setShowPassword={setShowPassword} />
                </TabsContent>
              </CardContent>
            
              <CardFooter className="flex flex-col space-y-4">
                <div className="relative w-full">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-vibebops-border"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-vibebops-card px-2 text-vibebops-textMuted">Or continue with</span>
                  </div>
                </div>
                
                <div className="w-full">
                  <GoogleAuthButton mode={activeTab as 'login' | 'signup'} />
                </div>
              </CardFooter>
            </Tabs>
          </Card>
          
          {/* Additional decorative elements near the card */}
          <div className="absolute -right-10 -bottom-5 text-vibebops-accent opacity-40">
            <Share2 size={32} className="transform rotate-45" />
          </div>
        </div>
      </div>
      
      {/* Footer with subtle brand elements */}
      <div className="relative z-10 pb-4 text-center text-vibebops-textMuted/50 text-xs">
        <p>VibeBops ⓒ 2024 • Social Networking for the Creative Tech Community</p>
      </div>
    </div>
  );
}
